import { UzFormField, UzInput, UzTextarea } from '@utazzo/ui';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';

import { AddRowButton, RowShell } from './RowShell';

import type { CreateWebsiteDestinationPageRequest } from '@utazzo/shared';

import { MediaDropField } from '@/features/website-cms/components/MediaDropField';
import { MediaGalleryField } from '@/features/website-cms/components/MediaGalleryField';

const MAX_REVIEW_IMAGES = 5;

function ReviewImageFields({ reviewIndex, disabled }: { reviewIndex: number; disabled?: boolean }) {
  return (
    <UzFormField name={`reviews.${reviewIndex}.imageMediaIds` as const}>
      {({ field }) => (
        <div className="lg:col-span-2">
          <MediaGalleryField
            label={`Trip photos (up to ${MAX_REVIEW_IMAGES})`}
            value={(field.value as string[] | undefined) ?? []}
            folder="destination-pages"
            disabled={disabled}
            maxItems={MAX_REVIEW_IMAGES}
            onChange={field.onChange}
          />
          <p className="mt-1 text-xs text-zinc-500">
            Select or drop multiple images at once. They are saved when you save the page.
          </p>
        </div>
      )}
    </UzFormField>
  );
}

export const ReviewsEditor = ({
  form,
  disabled,
}: {
  form: UseFormReturn<CreateWebsiteDestinationPageRequest>;
  disabled?: boolean;
}) => {
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'reviews',
  });
  return (
    <div className="space-y-3">
      {fields.map((f, i) => (
        <RowShell
          key={f.id}
          index={i}
          total={fields.length}
          disabled={disabled}
          onMove={move}
          onRemove={remove}
        >
          <UzFormField name={`reviews.${i}.customerName` as const}>
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null) ?? ''}
                label="Customer name"
                error={error}
                disabled={disabled}
              />
            )}
          </UzFormField>
          <UzFormField name={`reviews.${i}.summary` as const}>
            {({ field, error }) => (
              <UzTextarea
                {...field}
                value={(field.value as string | null) ?? ''}
                label="Review"
                rows={3}
                error={error}
                disabled={disabled}
              />
            )}
          </UzFormField>
          <UzFormField name={`reviews.${i}.rating` as const}>
            {({ field, error }) => (
              <UzInput
                type="number"
                min={1}
                max={5}
                name={field.name}
                value={typeof field.value === 'number' ? String(field.value) : ''}
                onChange={(e) => field.onChange(Number(e.target.value))}
                label="Rating (1-5)"
                error={error}
                disabled={disabled}
              />
            )}
          </UzFormField>
          <UzFormField name={`reviews.${i}.portraitMediaId` as const}>
            {({ field }) => (
              <div className="space-y-1">
                <label className="text-xs font-medium text-zinc-600">Portrait (optional)</label>
                <MediaDropField
                  value={(field.value as string | null) ?? null}
                  kind="image"
                  folder="destination-pages"
                  disabled={disabled}
                  onChange={(id) => field.onChange(id)}
                />
              </div>
            )}
          </UzFormField>
          <ReviewImageFields reviewIndex={i} disabled={disabled} />
        </RowShell>
      ))}
      <AddRowButton
        label="Add review"
        disabled={disabled}
        onClick={() =>
          append({
            customerName: '',
            summary: '',
            rating: 5,
            portraitMediaId: null,
            imageMediaIds: [],
          })
        }
      />
    </div>
  );
};
